/* eslint-disable no-shadow */
/* eslint-disable no-else-return */
/* eslint-disable react/no-array-index-key */
/* eslint-disable react/prop-types */
import React, { useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Flex, Button } from "orcs-design-system";
import parseHTMLToReact from "html-react-parser";
import styled from "styled-components";
import { useScrollToBottom } from "src/hooks/useScrollToBottom";
import { AIMessage } from "./AIMessage";
import { ToolMessage } from "./ToolMessage";
import { createHtmlParsingOptions } from "./HTMLElementOverrides";
import getDisplayMessages from "./utils/getDisplayMessages";
import { useAIChat } from "./context/AIChatContext";

/**
 * Styled wrapper component for AI messages
 * @component
 * @example
 * <AIMessageWrapper>
 *   <AIMessage content="Hello world" />
 * </AIMessageWrapper>
 *
 * @deprecated
 */
const AIMessageWrapper = styled(Box)`
  min-width: 95%;
  max-width: 95%;
`;

/**
 * AIChat component for handling AI-powered chat interactions
 * @param {Object} props - Component props
 * @param {Object} props.htmlElementOverrides - Custom HTML element overrides for parsing
 * @returns {JSX.Element|null} The AIChat component or null if disabled
 */
export const AIChat = ({ htmlElementOverrides }) => {
  const {
    messages,
    previousMessages,
    complete,
    hasError,
    setUserHasScrolledUp,
    userHasScrolledUp,
    onRetry,
  } = useAIChat();

  /** @type {Object} Scroll-to-bottom hook interface */
  const { scrollContainerRef, scrollToBottom, handleScroll } =
    useScrollToBottom({
      threshold: 100,
      setUserHasScrolledUp,
      userHasScrolledUp,
    });

  /**
   * Effect for auto-scrolling chat to bottom on new messages
   * @effect
   * @description Automatically scrolls to bottom when new content arrives,
   * but respects user's manual scroll position
   * @param {Array<Object>} messages - Current messages array
   * @param {Array<Object>} previousMessages - Previous messages for comparison
   * @param {boolean} userHasScrolledUp - Whether user scrolled up manually
   * @performance Uses requestAnimationFrame for smooth scrolling
   */
  useEffect(() => {
    if (messages.length > 0) {
      /** @type {boolean} Whether new content has been added */
      const hasNewContent =
        JSON.stringify(messages) !== JSON.stringify(previousMessages);

      /** @type {boolean} Whether we should automatically scroll */
      const shouldAutoScroll = hasNewContent && !userHasScrolledUp;

      if (shouldAutoScroll) {
        requestAnimationFrame(scrollToBottom);
      }
    }
  }, [messages, previousMessages, userHasScrolledUp, scrollToBottom]);

  /** @type {Object} HTML parsing options with custom element overrides */
  const options = useMemo(
    () => createHtmlParsingOptions(htmlElementOverrides),
    [htmlElementOverrides]
  );

  const renderMessages = useCallback(() => {
    /**
     * Renders welcome message when chat is empty and complete
     * @returns {JSX.Element} Welcome message component
     */
    if (messages.length === 0 && complete) {
      return (
        // Welcome Message
        <Flex justifyContent="flex-start">
          <AIMessageWrapper>
            <AIMessage
              complete={true}
              content="Hi, I'm your TeamForm assistant! How can I help?"
              options={options}
              showAIWarning={false}
            />
          </AIMessageWrapper>
        </Flex>
      );
    }

    const displayMessages = getDisplayMessages(messages);

    /**
     * Renders grouped messages with appropriate styling and components
     * @returns {Array<JSX.Element>} Array of rendered message components
     */
    return displayMessages
      .map((message, groupIndex) => {
        /**
         * Render assistant message with AI styling
         * @returns {JSX.Element} Assistant message component
         */
        if (message.role === "assistant") {
          const displayContent = message.content;
          const { thoughts = [], tool_calls: toolCalls = [] } = message;
          const lastThought = thoughts[thoughts.length - 1];
          const lastToolCall = toolCalls[toolCalls.length - 1];

          return (
            <Flex
              flexDirection="column"
              justifyContent="flex-start"
              mt="4px"
              mb="4px"
              gap="8px"
              key={`assistant-${groupIndex}`}
            >
              {lastToolCall && (
                <AIMessageWrapper>
                  <ToolMessage
                    message={lastToolCall}
                    allToolsCalled={toolCalls}
                  />
                </AIMessageWrapper>
              )}
              {lastThought && !displayContent && (
                <AIMessageWrapper>
                  <AIMessage
                    complete={lastThought?.complete}
                    content={lastThought?.content}
                    options={options}
                    showAIWarning={false}
                  />
                </AIMessageWrapper>
              )}
              {displayContent && (
                <AIMessageWrapper>
                  <AIMessage
                    complete={
                      groupIndex === displayMessages.length - 1
                        ? message.complete
                        : true
                    }
                    content={displayContent}
                    options={options}
                    showAIWarning={false}
                  />
                </AIMessageWrapper>
              )}
            </Flex>
          );
        } else if (message.role === "tool") {
          /**
           * Render tool message with count indicator
           * @returns {JSX.Element} Tool message component
           */
          return (
            <Flex
              justifyContent="flex-start"
              mt="4px"
              mb="4px"
              key={`tool-group-${groupIndex}`}
            >
              <AIMessageWrapper>
                <ToolMessage
                  toolName={message.content}
                  complete={message.complete}
                  count={message.count}
                />
              </AIMessageWrapper>
            </Flex>
          );
        } else if (message.role === "user") {
          /**
           * Render user message with right alignment
           * @returns {JSX.Element} User message component
           */
          return (
            <Flex
              justifyContent="flex-end"
              mt="4px"
              mb="4px"
              key={`user-${groupIndex}`}
            >
              <Box
                background="white"
                color="greyDarkest"
                padding="6px 8px"
                borderRadius={2}
                maxWidth="95%"
                border="1px solid"
                borderColor="greyLight"
                boxShadow="0 2px 8px rgba(0,0,0,0.1)"
                style={{ wordWrap: "break-word" }}
              >
                {parseHTMLToReact(message.content, options)}
              </Box>
            </Flex>
          );
        } else {
          /**
           * Unknown message role - skip rendering
           * @returns {null}
           */
          return null;
        }
      })
      .filter(Boolean);
  }, [messages, complete, options]);

  return (
    <Box
      ref={scrollContainerRef}
      borderRadius={2}
      width="100%"
      height="100%"
      display="flex"
      flexDirection="column"
      overflowY="auto"
      padding="8px"
      onScroll={handleScroll}
    >
      {renderMessages()}
      {hasError && (
        <div
          style={{
            paddingTop: "2px",
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Button
            height="25px"
            width="75px"
            aria-label="Return"
            onClick={onRetry}
          >
            Retry
          </Button>
        </div>
      )}
    </Box>
  );
};

/**
 * PropTypes validation for AIChat component
 * @type {Object}
 * @property {any} [htmlElementOverrides] - Optional custom HTML element overrides
 */
AIChat.propTypes = {
  htmlElementOverrides: PropTypes.any,
};
